/**
 * This is a custom input component extended from ShadedUI that supports a prefix and a wrapper.
 *
 * @see https://ui.shadcn.com/docs/components/input
 */
import * as React from "react"
import { cn } from "../../lib/utils"

const Input = React.forwardRef<
  HTMLInputElement,
  /**
   * The issue was that the native HTML input element has a prefix attribute that conflicts with your custom prefix prop. 
   * By using Omit<React.ComponentProps<"input">, "prefix"> we:
   * Remove the native prefix attribute from the input props
   * Add our custom prefix prop with the correct type React.ReactNode | null
   */
  Omit<React.ComponentProps<"input">, "prefix"> & {
    prefix?: React.ReactNode | null
    prefixClassName?: string
    wrapperClassName?: string
  }
>(
  ({ className, type, prefix, prefixClassName, wrapperClassName, ...props }, ref) => {
    if (!prefix) {
      return (
        <input
          type={type}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className
          )}
          ref={ref}
          {...props}
          style={{
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
          }}
        />
      )
    }

    return (
      <div
        className={cn(
          "flex h-10 w-full overflow-hidden rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          wrapperClassName
        )}
      >
        <div
          className={cn(
            "flex items-center justify-center px-3 text-sm text-muted-foreground",
            prefixClassName
          )}
        >
          {prefix}
        </div>
        <input
          type={type}
          className={cn(
            "h-full w-full border-0 bg-transparent px-2 py-2 text-base focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className
          )}
          ref={ref}
          {...props}
          style={{
            WebkitAppearance: "none",
            MozAppearance: "none",
            appearance: "none",
          }}
        />
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }